import React, { useState, useEffect } from "react";
import { Table, Input, DatePicker, Select, Card, Modal, Button, message, Space, Row, Col, Divider, Typography, Tag } from "antd";
import { debounce } from "lodash";
import api from "../../api";
import moment from "moment";
import { SearchOutlined } from "@ant-design/icons";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

const PriceChangeLog = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  
  // برای جدول داخل مودال
  const [modalItems, setModalItems] = useState([]);
  const [modalPagination, setModalPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [modalFilters, setModalFilters] = useState({
    search: "",
    date: null,
  });
  const [modalId, setModalId] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    date: null,
    type: null,
  });

  // فانکشن جدید برای دریافت آیتم‌های مودال با فیلتر و پیجینیشن
  const fetchModalItems = async (
    id,
    page = 1,
    perPage = modalPagination.pageSize,
    filters = {}
  ) => {
    setModalLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        search: filters.search || "",
        date: filters.date || "",
        includes: ["price_change_log"],
      };
      
      const response = await api.get(
        `/panel/setting/price-change-items/${id}`,
        { params }
      );
      
      setModalData(response.data.data);
      setModalItems(response.data.data.items || []);
      setModalPagination({
        current: response.data.meta?.current_page || 1,
        pageSize: response.data.meta?.per_page || 10,
        total: response.data.meta?.total || 0,
      });
    } catch (error) {
      console.error("خطا در دریافت داده‌های مودال:", error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleViewDetails = async (id) => {
    setModalLoading(true);
    setIsModalVisible(true);
    setModalId(id);
    setModalFilters({ search: "", date: null });
    setModalPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });

    try {
      fetchModalItems(id, 1, 10, { search: "", date: null });
    } catch (error) {
      console.error("خطا در دریافت داده‌ها:", error);
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setModalData(null);
    setModalItems([]);
    setModalId(null);
  };

  const handleRevertPrice = async (id) => {
    try {
      const response = await api.get(
        `/panel/setting/price-change-log/${id}/revert`
      );
      message.success(response.data.message);
      fetchData(pagination.current, pagination.pageSize, filters);
    } catch (error) {
      console.error("خطا در برگرداندن قیمت:", error);
      message.error("خطا در برگرداندن قیمت");
    }
  };

  const fetchData = async (
    page = 1,
    perPage = pagination.pageSize,
    filters = {}
  ) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        ...filters,
      };
      const response = await api.get("/panel/setting/price-change-log", {
        params,
      });
      setData(response.data.data);
      setPagination({
        current: response.data.meta.current_page,
        pageSize: response.data.meta.per_page,
        total: response.data.meta.total,
      });
    } catch (error) {
      console.error("خطا در دریافت داده‌ها:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, pagination.pageSize, filters);
  }, [filters, pagination.pageSize]);

  // برای جستجو در مودال
  const handleModalSearch = debounce((value) => {
    setModalFilters((prevFilters) => ({ ...prevFilters, search: value }));
    if (modalId) {
      fetchModalItems(modalId, 1, modalPagination.pageSize, { ...modalFilters, search: value });
    }
  }, 300);

  const handleSearch = debounce((value) => {
    setFilters((prevFilters) => ({ ...prevFilters, search: value }));
  }, 300);

  const handleFilterChange = (key, value) => {
    setFilters((prevFilters) => ({ ...prevFilters, [key]: value }));
  };

  const handlePageSizeChange = (current, pageSize) => {
    setPagination((prevPagination) => ({ ...prevPagination, pageSize }));
    fetchData(1, pageSize, filters);
  };

  // برای تغییر صفحه جدول مودال
  const handleModalTableChange = (pagination) => {
    fetchModalItems(
      modalId, 
      pagination.current, 
      pagination.pageSize, 
      modalFilters
    );
  };

  const columns = [
    {
      title: "شناسه ",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "ایجاد کننده",
      dataIndex: "creator",
      key: "creator",
      render: (creator) => creator.name,
    },

    {
      title: "بخش مربوطه",
      dataIndex: "subject",
      key: "subject",
      render: (subject) => (
        <>
          <p>
            <strong>نوع:</strong> {subject.type}
          </p>
          <p>
            <strong>کد بخش:</strong> {subject.id}
          </p>
          <p>
            <strong>عنوان:</strong> {subject.name}
          </p>
          {/* نمایش کتگوری‌ها */}
          {subject.categories && subject.categories.length > 0 && (
            <div>
              <strong>دسته‌بندی‌ها:</strong>{" "}
              {subject.categories.map(category => (
                <Tag key={category.id} color="blue" className="mt-1 mb-1">
                  {category.name}
                </Tag>
              ))}
            </div>
          )}
        </>
      ),
    },
    {
      title: "نوع تغییر",
      dataIndex: "type",
      key: "type",
      render: (type, record) => (
        <>
          <p>
            <strong>نوع:</strong>{" "}
            {type === "increase"
              ? "افزایش"
              : type === "decrease"
              ? "کاهش"
              : type}
          </p>
          <p>
            <strong>درصد:</strong> {record.percentage}%
          </p>
        </>
      ),
    },

    {
      title: "تاریخ ایجاد",
      dataIndex: "created_at",
      key: "created_at",
    },

    {
      title: "عملیات",
      key: "actions",
      render: (_, record) => (
        <div>
          <Space>
          <Button type="primary" onClick={() => handleViewDetails(record.id)}>
            مشاهده
          </Button>
          <Button
            danger
            onClick={() => handleRevertPrice(record.id)}
          >
            برگرداندن قیمت
          </Button>
          </Space>
        </div>
      ),
    },
  ];

  const modalColumns = [
    { title: "شناسه", dataIndex: "id", key: "id" },
    {
      title: "محصول",
      dataIndex: "post",
      key: "post",
      render: (post) => `${post.title} (${post.technical_code})`,
    },
    {
      title: "برند",
      dataIndex: ["brand", "name"],
      key: "brand",
    },
    {
      title: "تامین کننده",
      dataIndex: ["seller", "name"],
      key: "seller",
    },
    {
      title: "گارانتی",
      dataIndex: ["guarantee", "name"],
      key: "guarantee",
      render: (name) => name || "-",
    },
    {
      title: "قیمت قدیمی",
      dataIndex: "old_price",
      key: "old_price",
      render: (price) => price?.toLocaleString() || "-",
    },
    {
      title: "قیمت جدید",
      dataIndex: "new_price",
      key: "new_price",
      render: (price) => price?.toLocaleString() || "-",
    },
    {
      title: "تاریخ ایجاد",
      dataIndex: "created_at",
      key: "created_at",
    },
  ];

  return (
    <Card title="گزارش تغییرات قیمت">
      <div className="mb-4 flex items-center">
        <Input
          placeholder="جستجو..."
          allowClear
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: 200, marginRight: 8 }}
        />

        <Select
          className="mr-4"
          allowClear
          placeholder="نوع تغییر"
          style={{ width: 120 }}
          onChange={(value) => handleFilterChange("type", value)}
        >
          <Option value="increase">افزایش</Option>
          <Option value="decrease">کاهش</Option>
        </Select>
      </div>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: (page) => fetchData(page, pagination.pageSize, filters),
          onShowSizeChange: handlePageSizeChange,
          showSizeChanger: true,
          pageSizeOptions: ['10', '25', '50', '100'],
        }}
        rowKey="id"
      />

      <Modal
        title={<Title level={4}>جزئیات تغییرات قیمت</Title>}
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={900}
        bodyStyle={{ padding: '24px' }}
      >
        {modalLoading && !modalData ? (
          <div className="text-center py-8">در حال بارگذاری...</div>
        ) : (
          modalData && (
            <div>
              <Card className="mb-4" bordered={false} style={{ background: '#f5f5f5' }}>
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <Text strong>ایجاد کننده:</Text> {modalData.price_change_log?.creator?.name}
                  </Col>
                  <Col span={12}>
                    <Text strong>درصد:</Text> {modalData.price_change_log?.percentage}%
                  </Col>
                  <Col span={12}>
                    <Text strong>نوع:</Text>{" "}
                    {modalData.price_change_log?.type === "increase"
                      ? "افزایش"
                      : modalData.price_change_log?.type === "decrease"
                      ? "کاهش"
                      : modalData.price_change_log?.type}
                  </Col>
                  <Col span={12}>
                    <Text strong>تاریخ ایجاد:</Text> {modalData.price_change_log?.created_at}
                  </Col>
                  {/* نمایش اطلاعات بخش مربوطه */}
                  <Col span={12}>
                    <Text strong>نوع بخش:</Text> {modalData.price_change_log?.subject?.type}
                  </Col>
                  <Col span={12}>
                    <Text strong>عنوان بخش:</Text> {modalData.price_change_log?.subject?.name}
                  </Col>
                  {/* نمایش کتگوری‌ها در مودال */}
                  {modalData.price_change_log?.subject?.categories && 
                   modalData.price_change_log?.subject?.categories.length > 0 && (
                    <Col span={24}>
                      <Text strong>دسته‌بندی‌ها:</Text>{" "}
                      {modalData.price_change_log?.subject?.categories.map(category => (
                        <Tag key={category.id} color="blue" style={{ margin: '0 4px 4px 0' }}>
                          {category.name}
                        </Tag>
                      ))}
                    </Col>
                  )}
                </Row>
              </Card>

              <Divider />
              
              <div className="mb-4">
                <Row gutter={16} align="middle">
                  <Col>
                    <Input
                      placeholder="جستجوی محصول..."
                      prefix={<SearchOutlined />}
                      allowClear
                      onChange={(e) => handleModalSearch(e.target.value)}
                      style={{ width: 250 }}
                    />
                  </Col>
                </Row>
              </div>

              <Table
                dataSource={modalItems}
                columns={modalColumns}
                loading={modalLoading}
                rowKey="id"
                pagination={{
                  current: modalPagination.current,
                  pageSize: modalPagination.pageSize,
                  total: modalPagination.total,
                  showSizeChanger: true,
                  pageSizeOptions: ['5', '10', '20', '50'],
                }}
                onChange={handleModalTableChange}
                scroll={{ x: 'max-content' }}
                size="middle"
              />
            </div>
          )
        )}
      </Modal>
    </Card>
  );
};

export default PriceChangeLog;